import type { ResearchState } from '@galaxis/shared';
import { KIND_LABEL, RARITY_LABEL, fmtDuration, rarityClass, startBlock, storagePreview, thousands, type AreaClient, type TechClient } from './research-format.ts';

interface CardProps {
  tech: TechClient;
  area: AreaClient;
  state: ResearchState;
  live: { metal: number; energy: number; metalCapacity: number | null } | null;
  busy: boolean;
  onStart: (t: TechClient) => void;
  onOpen: (t: TechClient) => void;
  /** Hand cards are drawn bigger (Stellaris-like); spine entries are a compact row. */
  variant: 'card' | 'row';
}

/** Cost line with every part coloured by whether the stock covers it. */
export function CostLine({ tech, area, live }: { tech: TechClient; area: AreaClient; live: CardProps['live'] }) {
  const c = tech.effectiveCost;
  const part = (ok: boolean, text: string, testId: string) => (
    <span className={ok ? 'text-slate-300' : 'text-red-400'} data-ok={ok} data-testid={testId}>{text}</span>
  );
  return (
    <span className="font-mono text-[11px] flex flex-wrap gap-x-2" data-testid={`cost-${tech.id}`}>
      {part(area.rp.balance >= c.rp, `${c.rp} RP`, `cost-rp-${tech.id}`)}
      {part(!!live && live.metal >= c.metal, `${thousands(c.metal)} fém`, `cost-metal-${tech.id}`)}
      {part(!!live && live.energy >= c.energy, `${thousands(c.energy)} en.`, `cost-energy-${tech.id}`)}
      <span className="text-slate-400">{fmtDuration(tech.timeMs)}</span>
      {c.metal !== tech.cost.metal && <span className="text-emerald-400">roncsmező −50%</span>}
    </span>
  );
}

function StatusTag({ tech }: { tech: TechClient }) {
  if (tech.status === 'done') return <span className="text-xs text-emerald-400">✓ Kész</span>;
  if (tech.status === 'researching') return <span className="text-xs text-accent">Folyamatban</span>;
  if (tech.status === 'queued') return <span className="text-xs text-accent">Sorban: {tech.queuePosition}.</span>;
  return null;
}

export function lockText(t: TechClient): string {
  if (t.lockReason === 'prerequisite') return `Hiányzik: ${(t.missing ?? []).join(', ')}`;
  if (t.lockReason === 'tier') return `A ${t.tier}. szint még zárolt: az előző szintből 4 kutatás kell ebben a területben`;
  if (t.lockReason === 'not_in_hand') return 'Kártyás technológia: csak akkor választható, ha előkerül a lehetőségek között';
  if (t.lockReason === 'discovery') return 'Csak felfedezéssel szerezhető meg';
  return '';
}

export function TechCard({ tech: t, area, state, live, busy, onStart, onOpen, variant }: CardProps) {
  const block = t.status === 'available' ? startBlock(t, area, live) : null;
  const willQueue = area.slots.used >= area.slots.total || area.queue.length > 0;
  const storage = storagePreview(t, state, live?.metalCapacity ?? null);
  const tone = t.status === 'done' ? 'border-emerald-500/40' : t.status === 'researching' ? 'border-accent/60' : t.status === 'queued' ? 'border-accent/30' : t.status === 'locked' ? 'border-slate-800 opacity-70' : rarityClass(t);
  const big = variant === 'card';
  return (
    <li
      className={`sci-fi-panel rounded-lg border ${tone} ${big ? 'p-3' : 'p-2.5'}`}
      data-testid={`tech-${t.id}`} data-status={t.status} data-kind={t.kind} data-rarity={t.rarity} data-tier={t.tier} data-area={t.area}
    >
      <div className="flex items-start justify-between gap-2">
        <button type="button" className="min-h-11 -my-1 text-left text-sm font-medium text-slate-100 hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent rounded"
          onClick={() => onOpen(t)} aria-haspopup="dialog" data-testid={`open-${t.id}`}>
          {t.name}
        </button>
        <span className="flex flex-col items-end gap-0.5 text-[10px] shrink-0">
          <span className="text-slate-400" aria-label={`${t.tier}. szint`}>{'▪'.repeat(t.tier)} {t.tier}. szint</span>
          <span className={t.rarity === 'rare' ? 'text-violet-300' : 'text-slate-500'}>{KIND_LABEL[t.kind]} · {RARITY_LABEL[t.rarity]}</span>
        </span>
      </div>
      {(big || t.status === 'locked') && (
        <ul className="mt-1 text-xs text-emerald-300/90 list-disc list-inside" data-testid={`unlocks-${t.id}`}>
          {t.unlocks.slice(0, big ? 4 : 2).map(u => <li key={u}>{u}</li>)}
        </ul>
      )}
      {storage && <p className="text-[11px] text-sky-300 mt-1" data-testid={`storage-preview-${t.id}`}>Raktár: {thousands(storage.from)} → {thousands(storage.to)}</p>}
      {t.requires.length > 0 && big && t.status !== 'locked' && <p className="text-[11px] text-slate-500 mt-1">Előfeltétel: {t.requires.map(r => state.techs.find(x => x.id === r)?.name ?? r).join(', ')}</p>}
      {t.status === 'locked' && <p className="text-[11px] text-amber-400 mt-1" data-testid={`lock-${t.id}`}>{lockText(t)}</p>}
      {t.status !== 'locked' && (
        <div className="flex items-center justify-between gap-2 mt-2">
          <CostLine tech={t} area={area} live={live} />
          <StatusTag tech={t} />
        </div>
      )}
      {t.status === 'available' && (
        <>
          <button type="button" className="btn-primary min-h-11 w-full text-xs mt-2 disabled:opacity-40" data-testid={`start-${t.id}`}
            disabled={busy || !!block} title={block ?? (willQueue ? 'Minden hely foglalt: sorba kerül' : undefined)}
            aria-describedby={block ? `why-${t.id}` : undefined}
            onClick={() => onStart(t)}>
            {willQueue ? 'Sorba állítás' : 'Kutatás indítása'}
          </button>
          {block && <p id={`why-${t.id}`} className="text-[11px] text-amber-400 mt-1">{block}</p>}
        </>
      )}
    </li>
  );
}
